import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, API, useTheme } from '@/App';
import { useTranslation } from '@/hooks/useTranslation';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import Sidebar from '@/components/Sidebar';
import WhatsAppEmbeddedSignup from '@/components/WhatsAppEmbeddedSignup';
import { DashboardAnalytics } from '@/components/DashboardAnalytics';
import { PWAInstallBanner } from '@/components/PWAComponents';
import { ConversionFunnel, ChannelPerformance, CustomerInsights, ResponseTimeCard } from '@/components/DashboardInsights';
import { DashboardCard, CardHeader, EmptyState, ChannelFilter } from '@/components/dashboard';
import { KPICard, MiniKPI } from '@/components/dashboard';
import AIInsightsWidget from '@/components/dashboard/AIInsightsWidget';
import { QuotaCard } from '@/components/QuotaCard';
import { QuotaWarningBanner } from '@/components/QuotaWarningBanner';
import { FaFacebookMessenger, FaWhatsapp } from 'react-icons/fa';
import {
  MessageSquare,
  Plus,
  BarChart3,
  Settings,
  LogOut,
  Home,
  Bot,
  Menu,
  X,
  Trash2,
  ExternalLink,
  Calendar,
  Crown,
  Zap,
  TrendingUp,
  Users,
  Instagram,
  Globe,
  CheckCircle,
  Link as LinkIcon,
  Clock,
  AlertTriangle,
  Package,
  Bell,
  Eye,
  ChevronRight,
  ShoppingCart,
  DollarSign,
  Activity,
  ArrowUpRight,
  Filter,
  Sparkles,
  Building2,
  LayoutGrid
} from 'lucide-react';

// ============================================================================
// TRIAL COUNTDOWN COMPONENT
// ============================================================================
const TrialCountdown = ({ expiresAt, onExpired, compact = false }) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [isExpired, setIsExpired] = useState(false);
  const [expiredCalled, setExpiredCalled] = useState(false);

  useEffect(() => {
    if (!expiresAt) return;

    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const expiry = new Date(expiresAt).getTime();
      const difference = expiry - now;

      if (difference <= 0) {
        setIsExpired(true);
        if (!expiredCalled) {
          setExpiredCalled(true);
          onExpired?.();
        }
        return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      }

      return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((difference % (1000 * 60)) / 1000)
      };
    };

    setTimeLeft(calculateTimeLeft());
    const timer = setInterval(() => setTimeLeft(calculateTimeLeft()), 1000);
    return () => clearInterval(timer);
  }, [expiresAt, onExpired, expiredCalled]);

  const padZero = (num) => String(num).padStart(2, '0');

  if (isExpired) {
    return (
      <div className="flex items-center gap-1.5 text-red-600 dark:text-red-400 text-sm">
        <Clock className="w-4 h-4" />
        <span className="font-medium">Trial berakhir</span>
      </div>
    );
  }

  if (compact) {
    return (
      <div className="flex items-center gap-1.5 text-amber-600 dark:text-amber-400 text-xs sm:text-sm">
        <Clock className="w-3.5 h-3.5" />
        <span className="font-medium font-mono">
          {timeLeft.days > 0 && `${timeLeft.days}h `}
          {padZero(timeLeft.hours)}:{padZero(timeLeft.minutes)}:{padZero(timeLeft.seconds)}
        </span>
      </div>
    );
  }

  return null;
};

// ============================================================================
// MAIN DASHBOARD COMPONENT
// ============================================================================
const Dashboard = () => {
  const { user, logout, token } = useAuth();
  const { theme } = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  // State management
  const [aiAgents, setAiAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newBotName, setNewBotName] = useState('');
  const [newBotDescription, setNewBotDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const [planLimits, setPlanLimits] = useState(null);
  const [channels, setChannels] = useState([]);
  const [channelsLoading, setChannelsLoading] = useState(true);
  const [lowStockProducts, setLowStockProducts] = useState([]);
  const [analyticsPeriod, setAnalyticsPeriod] = useState('week');
  const [selectedChannel, setSelectedChannel] = useState('all'); // Channel filter state
  const [recentOrders, setRecentOrders] = useState([]);
  const [expandedRecentOrders, setExpandedRecentOrders] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [weeklyRevenue, setWeeklyRevenue] = useState([0, 0, 0, 0, 0, 0, 0]);
  const [channelStats, setChannelStats] = useState({});
  const [onboardingSkipped, setOnboardingSkipped] = useState(false);
  const [showSetupBanner, setShowSetupBanner] = useState(true);
  
  // Stats comparison with yesterday
  const [ordersToday, setOrdersToday] = useState(0);
  const [ordersYesterday, setOrdersYesterday] = useState(0);
  const [chatsToday, setChatsToday] = useState(0);
  const [chatsYesterday, setChatsYesterday] = useState(0);
  const [allConversations, setAllConversations] = useState([]);
  
  // Notification states
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [readAlerts, setReadAlerts] = useState(() => {
    const saved = localStorage.getItem('nerava_read_alerts');
    return saved ? JSON.parse(saved) : [];
  });

  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  // Channel definitions for ChannelFilter component
  // Using LayoutGrid icon for "Semua" which is more recognizable
  const filterChannels = useMemo(() => [
    { type: 'all', name: 'Semua', icon: LayoutGrid, color: 'blue' },
    { type: 'whatsapp', name: 'WhatsApp', icon: FaWhatsapp, color: 'green' },
    { type: 'messenger', name: 'Messenger', icon: FaFacebookMessenger, color: 'blue' },
    { type: 'instagram', name: 'Instagram', icon: Instagram, color: 'pink' },
    { type: 'website', name: 'Website', icon: Globe, color: 'amber' }
  ], []);

  // Stock alerts (hanya produk fisik — endpoint low-stock sudah menyaring produk digital)
  const stockAlerts = lowStockProducts.map(product => ({
    id: product.id || product._id,
    name: product.name,
    stock: product.stock,
    type: product.stock === 0 ? 'out_of_stock' : 'low_stock',
    message: product.stock === 0 
      ? `Stok ${product.name} habis` 
      : `Stok ${product.name} tinggal ${product.stock}`
  }));

  const unreadAlerts = stockAlerts.filter(alert => !readAlerts.includes(alert.id));
  const unreadCount = unreadAlerts.length;

  // ============================================================================
  // EFFECTS
  // ============================================================================
  
  useEffect(() => {
    localStorage.setItem('nerava_read_alerts', JSON.stringify(readAlerts));
  }, [readAlerts]);

  useEffect(() => {
    const checkOnboardingStatus = async () => {
      const skipped = localStorage.getItem('onboarding_skipped');
      if (skipped === 'true') {
        setOnboardingSkipped(true);
        return;
      }
      try {
        const response = await axios.get(`${API}/onboarding/status`, { headers });
        if (response.data?.skipped) {
          setOnboardingSkipped(true);
          localStorage.setItem('onboarding_skipped', 'true');
        }
      } catch (error) {
        console.error('Error checking onboarding status:', error);
      }
    };
    checkOnboardingStatus();
  }, []);

  useEffect(() => {
    const dismissed = localStorage.getItem('setup_banner_dismissed');
    if (dismissed === 'true') setShowSetupBanner(false);
  }, []);

  useEffect(() => {
    fetchAiAgents();
    fetchPlanLimits();
    fetchChannels();
    fetchLowStockProducts();
    fetchRecentOrders();
  }, []);

  useEffect(() => {
    if (!allOrders.length) {
      setTotalRevenue(0);
      setWeeklyRevenue([0, 0, 0, 0, 0, 0, 0]);
      setChannelStats({
        website: { chats: 0, orders: 0 },
        whatsapp: { chats: 0, orders: 0 },
        messenger: { chats: 0, orders: 0 },
        instagram: { chats: 0, orders: 0 }
      });
      return;
    }
    
    const now = new Date();
    let startDate;
    
    if (analyticsPeriod === 'today') {
      startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
    } else if (analyticsPeriod === 'week') {
      startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    } else if (analyticsPeriod === 'month') {
      startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    } else {
      startDate = new Date(2020, 0, 1);
    }
    
    const filteredOrders = allOrders.filter(order => {
      // Filter by time period
      if (!order.created_at && analyticsPeriod !== 'all') return false;
      if (order.created_at) {
        const orderDate = new Date(order.created_at);
        if (orderDate < startDate) return false;
      }
      
      // Filter by channel
      if (selectedChannel !== 'all') {
        const orderChannel = (order.channel || '').toLowerCase();
        if (selectedChannel === 'whatsapp' && !orderChannel.includes('whatsapp') && !orderChannel.includes('wa')) return false;
        if (selectedChannel === 'messenger' && !orderChannel.includes('messenger') && !orderChannel.includes('facebook')) return false;
        if (selectedChannel === 'instagram' && !orderChannel.includes('instagram') && !orderChannel.includes('ig')) return false;
        if (selectedChannel === 'website' && !orderChannel.includes('web') && orderChannel !== '') return false;
      }
      
      return true;
    });
    
    setRecentOrders(filteredOrders);
    
    // Build order summary (1 row per order, not per item)
    const expanded = [];
    filteredOrders.forEach(order => {
      const items = order.items && Array.isArray(order.items) && order.items.length > 0 ? order.items : null;
      const itemCount = items ? items.length : 1;
      const productSummary = items 
        ? (itemCount === 1 ? `${items[0].product || 'Produk'}` : `${items[0].product || 'Produk'} + ${itemCount - 1} lainnya`)
        : (order.product || 'Produk');
      const totalQty = items ? items.reduce((sum, i) => sum + (i.quantity || 1), 0) : (order.quantity || 1);
      
      expanded.push({
        id: order.id,
        order_id: order.order_id || order.id,
        customer_name: order.customer_name,
        product: productSummary,
        quantity: totalQty,
        total: order.total || 0,
        created_at: order.created_at,
        status: order.status,
        item_count: itemCount
      });
    });
    setExpandedRecentOrders(expanded);
    
    // Calculate revenue - ONLY from paid orders (Paid, Shipped, Completed)
    const paidStatuses = ['paid', 'shipped', 'completed', 'sudah bayar', 'dikirim', 'selesai'];
    const revenue = filteredOrders.reduce((sum, order) => {
      // Only count revenue from paid orders
      const status = (order.status || '').toLowerCase();
      if (!paidStatuses.some(s => status.includes(s))) {
        return sum; // Skip unpaid orders
      }
      
      const subtotal = order.subtotal || 0;
      if (subtotal > 0) return sum + subtotal;
      const total = order.total || 0;
      const shipping = order.shipping_cost || 0;
      return sum + Math.max(0, total - shipping);
    }, 0);
    setTotalRevenue(revenue);
    
    // Calculate trend data
    const trendData = new Array(7).fill(0);
    const stats = {
      website: { chats: 0, orders: 0 },
      whatsapp: { chats: 0, orders: 0 },
      messenger: { chats: 0, orders: 0 },
      instagram: { chats: 0, orders: 0 }
    };
    
    filteredOrders.forEach(order => {
      // Only count paid orders for revenue trend
      const status = (order.status || '').toLowerCase();
      const isPaid = paidStatuses.some(s => status.includes(s));
      
      if (order.created_at && isPaid) {
        const orderDate = new Date(order.created_at);
        const diffDays = Math.floor((now - orderDate) / (1000 * 60 * 60 * 24));
        if (diffDays >= 0 && diffDays < 7) {
          const subtotal = order.subtotal || 0;
          const productRevenue = subtotal > 0 ? subtotal : Math.max(0, (order.total || 0) - (order.shipping_cost || 0));
          trendData[6 - diffDays] += productRevenue;
        }
      }
      
      const channel = (order.channel || 'website').toLowerCase();
      if (channel.includes('web') || channel.includes('Website')) stats.website.orders++;
      else if (channel.includes('whatsapp') || channel.includes('wa')) stats.whatsapp.orders++;
      else if (channel.includes('messenger') || channel.includes('facebook')) stats.messenger.orders++;
      else if (channel.includes('instagram') || channel.includes('ig')) stats.instagram.orders++;
      else stats.website.orders++;
    });
    
    // Calculate orders today vs yesterday
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
    const yesterdayStart = new Date(todayStart.getTime() - 24 * 60 * 60 * 1000);
    
    const todayOrders = allOrders.filter(order => {
      if (!order.created_at) return false;
      const orderDate = new Date(order.created_at);
      return orderDate >= todayStart;
    }).length;
    
    const yesterdayOrders = allOrders.filter(order => {
      if (!order.created_at) return false;
      const orderDate = new Date(order.created_at);
      return orderDate >= yesterdayStart && orderDate < todayStart;
    }).length;
    
    setOrdersToday(todayOrders);
    setOrdersYesterday(yesterdayOrders);
    
    setWeeklyRevenue(trendData);
    setChannelStats(stats);
  }, [allOrders, analyticsPeriod, selectedChannel]);

  useEffect(() => {
    if (aiAgents.length > 0) fetchChannelChatStats();
  }, [aiAgents]);

  // Recalculate chatsToday/chatsYesterday when selectedChannel changes
  useEffect(() => {
    if (allConversations.length === 0) return;
    
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
    const yesterdayStart = new Date(todayStart.getTime() - 24 * 60 * 60 * 1000);
    
    let todayChats = 0;
    let yesterdayChats = 0;
    
    allConversations.forEach(conv => {
      const channel = (conv.channel || 'website').toLowerCase();
      
      // Filter by selectedChannel
      if (selectedChannel !== 'all') {
        if (selectedChannel === 'whatsapp' && !channel.includes('whatsapp') && !channel.includes('wa')) return;
        if (selectedChannel === 'messenger' && !channel.includes('messenger') && !channel.includes('facebook')) return;
        if (selectedChannel === 'instagram' && !channel.includes('instagram') && !channel.includes('ig')) return;
        if (selectedChannel === 'website' && !channel.includes('web') && channel !== '') return;
      }
      
      // Count chats today vs yesterday
      if (conv.created_at || conv.last_message_at) {
        const chatDate = new Date(conv.last_message_at || conv.created_at);
        if (chatDate >= todayStart) {
          todayChats++;
        } else if (chatDate >= yesterdayStart && chatDate < todayStart) {
          yesterdayChats++;
        }
      }
    });
    
    setChatsToday(todayChats);
    setChatsYesterday(yesterdayChats);
  }, [allConversations, selectedChannel]);

  // ============================================================================
  // FETCH FUNCTIONS
  // ============================================================================
  
  const fetchAiAgents = async () => {
    try {
      const response = await axios.get(`${API}/chatbots`, { headers, withCredentials: true });
      setAiAgents(response.data);
    } catch (error) {
      console.error('Error fetching aiAgents:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlanLimits = async () => {
    try {
      const response = await axios.get(`${API}/plan/limits`, { headers, withCredentials: true });
      setPlanLimits(response.data);
    } catch (error) {
      console.error('Error fetching plan limits:', error);
    }
  };

  const fetchLowStockProducts = async () => {
    try {
      const response = await axios.get(`${API}/products/low-stock?user_id=${user?.id}&threshold=10`, { headers });
      if (response.data.success) setLowStockProducts(response.data.products || []);
    } catch (error) {
      console.error('Error fetching low stock products:', error);
    }
  };

  const fetchChannels = async () => {
    try {
      setChannelsLoading(true);
      const response = await fetch(`${backendUrl}/api/channels`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) setChannels(data.channels || []);
    } catch (error) {
      console.error('Error fetching channels:', error);
    } finally {
      setChannelsLoading(false);
    }
  };

  const fetchRecentOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders?user_id=${user?.id}&limit=200`, { headers });
      if (response.data.orders) {
        setAllOrders(response.data.orders);
        setRecentOrders(response.data.orders);
      }
    } catch (error) {
      console.error('Error fetching recent orders:', error);
    }
  };
  
  const fetchChannelChatStats = async () => {
    try {
      const chatbot = aiAgents[0];
      if (!chatbot) return;
      
      const response = await axios.get(`${API}/conversations?chatbot_id=${chatbot.id}`, { headers });
      if (response.data.conversations) {
        const conversations = response.data.conversations;
        setAllConversations(conversations);
        
        const stats = { ...channelStats };
        const now = new Date();
        const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
        const yesterdayStart = new Date(todayStart.getTime() - 24 * 60 * 60 * 1000);
        
        let todayChats = 0;
        let yesterdayChats = 0;
        
        conversations.forEach(conv => {
          const channel = (conv.channel || 'website').toLowerCase();
          if (channel.includes('web')) stats.website = { ...stats.website, chats: (stats.website?.chats || 0) + 1 };
          else if (channel.includes('whatsapp') || channel.includes('wa')) stats.whatsapp = { ...stats.whatsapp, chats: (stats.whatsapp?.chats || 0) + 1 };
          else if (channel.includes('messenger')) stats.messenger = { ...stats.messenger, chats: (stats.messenger?.chats || 0) + 1 };
          else if (channel.includes('instagram')) stats.instagram = { ...stats.instagram, chats: (stats.instagram?.chats || 0) + 1 };
          
          // Count chats today vs yesterday
          if (conv.created_at || conv.last_message_at) {
            const chatDate = new Date(conv.last_message_at || conv.created_at);
            if (chatDate >= todayStart) {
              todayChats++;
            } else if (chatDate >= yesterdayStart && chatDate < todayStart) {
              yesterdayChats++;
            }
          }
        });
        
        setChatsToday(todayChats);
        setChatsYesterday(yesterdayChats);
        setChannelStats(stats);
      }
    } catch (error) {
      console.error('Error fetching chat stats:', error);
    }
  };

  // ============================================================================
  // HANDLERS
  // ============================================================================
  
  const markAlertAsRead = (alertId) => {
    if (!readAlerts.includes(alertId)) setReadAlerts(prev => [...prev, alertId]);
  };

  const markAllAlertsAsRead = () => {
    const allAlertIds = stockAlerts.map(alert => alert.id);
    setReadAlerts(allAlertIds);
    toast.success('Semua notifikasi telah dibaca');
  };

  const handleAlertClick = (alert) => {
    markAlertAsRead(alert.id);
    setNotificationOpen(false);
    navigate('/catalog');
  };

  const handleResumeOnboarding = async () => {
    try {
      await axios.post(`${API}/onboarding/resume`, {}, { headers });
      localStorage.removeItem('onboarding_skipped');
      localStorage.removeItem('onboarding_completed');
      navigate('/onboarding');
    } catch (error) {
      console.error('Error resuming onboarding:', error);
      toast.error('Gagal melanjutkan setup. Silakan coba lagi.');
    }
  };

  const dismissSetupBanner = () => {
    setShowSetupBanner(false);
    localStorage.setItem('setup_banner_dismissed', 'true');
  };

  const isChannelConnected = (channelType) => {
    return channels.some(c => c.channel_type === channelType && c.status !== 'disconnected');
  };

  const handleConnectChannel = (channelType) => {
    if (channelType === 'whatsapp') navigate('/whatsapp-onboarding');
    else if (channelType === 'messenger') navigate('/messenger-setup');
    else if (channelType === 'website') {
      if (aiAgents.length > 0) navigate(`/agent/${aiAgents[0].id}?tab=embed`);
      else {
        toast.error('Buat AI Agent terlebih dahulu untuk mendapatkan kode embed');
        setCreateDialogOpen(true);
      }
    } else if (channelType === 'instagram') {
      const instagramOAuthUrl = `https://www.instagram.com/oauth/authorize?force_reauth=true&client_id=1183763387300782&redirect_uri=https://nerava.id/api/instagram/oauth/callback&response_type=code&scope=instagram_business_basic%2Cinstagram_business_manage_messages&state=${user?.id}`;
      window.location.href = instagramOAuthUrl;
    } else toast.info(`Integrasi ${channelType} akan segera tersedia!`);
  };

  const createAiAgent = async () => {
    if (!newBotName.trim()) {
      toast.error(t('dashboard.noBots'));
      return;
    }
    setCreating(true);
    try {
      const response = await axios.post(`${API}/chatbots`, {
        name: newBotName,
        description: newBotDescription
      }, { headers, withCredentials: true });
      setAiAgents([...aiAgents, response.data]);
      setCreateDialogOpen(false);
      setNewBotName('');
      setNewBotDescription('');
      toast.success('AI Agent berhasil dibuat!');
      navigate(`/agent/${response.data.id}`);
      fetchPlanLimits();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Gagal membuat aiAgent';
      toast.error(errorMsg);
    } finally {
      setCreating(false);
    }
  };

  const deleteAiAgent = async (id) => {
    if (!window.confirm('Yakin ingin menghapus aiAgent ini?')) return;
    try {
      await axios.delete(`${API}/chatbots/${id}`, { headers, withCredentials: true });
      setAiAgents(aiAgents.filter(bot => bot.id !== id));
      toast.success('AI Agent berhasil dihapus');
      fetchPlanLimits();
    } catch (error) {
      toast.error('Gagal menghapus aiAgent');
    }
  };

  // ============================================================================
  // HELPER FUNCTIONS
  // ============================================================================
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const getPlanBadgeColor = (plan) => {
    const colors = {
      starter: 'bg-gradient-to-r from-blue-500 to-indigo-500',
      growth: 'bg-gradient-to-r from-purple-500 to-pink-500',
      business: 'bg-gradient-to-r from-amber-500 to-orange-500',
      enterprise: 'bg-gradient-to-r from-slate-600 to-slate-800'
    };
    return colors[plan?.toLowerCase()] || 'bg-gradient-to-r from-blue-600 to-indigo-600';
  };

  const getPlanName = (plan) => {
    const names = { trial: 'TRIAL', starter: 'STARTER', growth: 'GROWTH', business: 'BUSINESS', enterprise: 'ENTERPRISE', free: 'TRIAL' };
    return names[plan?.toLowerCase()] || 'TRIAL';
  };

  // Get plan icon based on plan type (matching PricingPlans.jsx)
  const getPlanIcon = (plan) => {
    const icons = {
      trial: Sparkles,
      free: Sparkles,
      starter: Zap,
      growth: TrendingUp,
      business: Crown,
      enterprise: Building2
    };
    const IconComponent = icons[plan?.toLowerCase()] || Sparkles;
    return <IconComponent className="w-3 h-3 sm:w-4 sm:h-4" />;
  };

  // Calculate totalChats based on selectedChannel + analyticsPeriod filter (consistent with orders)
  const totalChats = useMemo(() => {
    if (allConversations.length === 0) return 0;
    
    // Compute period start date (same logic as filteredOrders)
    const now = new Date();
    let startDate;
    if (analyticsPeriod === 'today') {
      startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
    } else if (analyticsPeriod === 'week') {
      startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    } else if (analyticsPeriod === 'month') {
      startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    } else {
      startDate = new Date(2020, 0, 1);
    }
    
    return allConversations.filter(conv => {
      // Filter by period using last_message_at fallback to created_at
      const timestamp = conv.last_message_at || conv.created_at;
      if (analyticsPeriod !== 'all') {
        if (!timestamp) return false;
        const convDate = new Date(timestamp);
        if (isNaN(convDate.getTime()) || convDate < startDate) return false;
      }
      
      // Filter by channel
      if (selectedChannel !== 'all') {
        const channel = (conv.channel || 'website').toLowerCase();
        if (selectedChannel === 'whatsapp') return channel.includes('whatsapp') || channel.includes('wa');
        if (selectedChannel === 'messenger') return channel.includes('messenger') || channel.includes('facebook');
        if (selectedChannel === 'instagram') return channel.includes('instagram') || channel.includes('ig');
        if (selectedChannel === 'website') return channel.includes('web') || channel === '';
      }
      return true;
    }).length;
  }, [allConversations, selectedChannel, analyticsPeriod]);

  // ============================================================================
  // RENDER
  // ============================================================================
  
  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'dark' : ''}`}>
      <div className="min-h-screen bg-slate-100 dark:bg-[#0A0A0A] transition-colors">
        <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
        
        {/* Main Content */}
        <div className="lg:ml-64">
          {/* ================================================================ */}
          {/* HEADER — compact, toolbar merged */}
          {/* ================================================================ */}
          <header className="sticky top-0 backdrop-blur-xl bg-white/85 dark:bg-[#0A0A0A]/85 border-b border-slate-200 dark:border-zinc-800/80 z-20">
            {/* Row 1: Menu + Title + Plan Badge */}
            <div className="px-3 sm:px-6 py-2.5 sm:py-3">
              <div className="flex items-center justify-between gap-2 sm:gap-4">
                <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                  <button onClick={() => setSidebarOpen(true)} className="lg:hidden text-slate-600 dark:text-zinc-300 flex-shrink-0" data-testid="mobile-menu-btn">
                    <Menu className="w-5 h-5" />
                  </button>
                  <h1 className="text-lg sm:text-2xl font-semibold tracking-tight text-slate-900 dark:text-zinc-50 truncate" style={{ fontFamily: 'Manrope' }} data-testid="page-title">
                    {t('dashboard.title')}
                  </h1>
                </div>

                <div className="flex items-center gap-1.5 sm:gap-3 flex-shrink-0">
                  {planLimits && (
                    <div className={`${getPlanBadgeColor(planLimits.plan)} px-2 py-1 rounded-md sm:rounded-lg text-white text-[9px] sm:text-xs font-semibold flex items-center gap-0.5 sm:gap-1`}>
                      {getPlanIcon(planLimits.plan)}
                      <span className="hidden xs:inline">{getPlanName(planLimits.plan)}</span>
                    </div>
                  )}
                  {planLimits?.plan === 'trial' && user?.trial_expires_at && (
                    <TrialCountdown expiresAt={user.trial_expires_at} onExpired={() => toast.error('Trial Anda telah berakhir!')} compact />
                  )}
                  {planLimits?.plan === 'trial' && (
                    <Button onClick={() => navigate('/payment?plan=starter')} size="sm" className="hidden sm:flex bg-indigo-600 hover:bg-indigo-700 text-white text-[10px] sm:text-xs px-2 sm:px-3 h-7 sm:h-8">
                      <Zap className="w-3 h-3 sm:w-3.5 sm:h-3.5" /><span className="ml-1">Upgrade</span>
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Row 2: Channel filter + Period + Notification — inline in header */}
            <div className="px-3 sm:px-6 pb-2.5 sm:pb-3 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 overflow-x-auto no-scrollbar" data-testid="channel-filter-bar">
                <ChannelFilter
                  selectedChannel={selectedChannel}
                  onChannelChange={setSelectedChannel}
                  channels={filterChannels}
                  isChannelConnected={isChannelConnected}
                  size="small"
                />
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <select 
                  className="text-[10px] sm:text-xs bg-slate-100 dark:bg-zinc-800 border-0 rounded-md px-2 py-1.5 text-slate-600 dark:text-zinc-300 font-medium w-16 sm:w-auto"
                  value={analyticsPeriod} onChange={(e) => setAnalyticsPeriod(e.target.value)}
                  data-testid="period-selector"
                >
                  <option value="all">Semua</option>
                  <option value="today">Hari ini</option>
                  <option value="week">7 Hari</option>
                  <option value="month">30 Hari</option>
                </select>
                <div className="relative flex-shrink-0" data-testid="notification-wrapper">
                  <button
                    onClick={() => setNotificationOpen(!notificationOpen)}
                    className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-zinc-800 flex items-center justify-center text-slate-500 hover:text-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors dark:text-zinc-200"
                    data-testid="notification-bell"
                  >
                    <Bell className="w-4 h-4" />
                  </button>
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">{unreadCount > 9 ? '9+' : unreadCount}</span>
                  )}
                  {notificationOpen && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setNotificationOpen(false)} />
                      <div className="absolute right-0 top-full mt-2 w-[calc(100vw-2rem)] sm:w-80 max-w-sm bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 shadow-2xl z-50">
                        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-zinc-800">
                          <h3 className="font-semibold text-slate-900 dark:text-zinc-50 text-sm flex items-center gap-2">
                            <Bell className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />Notifikasi
                          </h3>
                          {unreadCount > 0 && (
                            <button onClick={markAllAlertsAsRead} className="text-xs text-indigo-600 hover:text-indigo-700 dark:text-indigo-400">Baca Semua</button>
                          )}
                        </div>
                        <div className="max-h-80 overflow-y-auto">
                          {stockAlerts.length === 0 ? (
                            <EmptyState icon={CheckCircle} title="Tidak ada notifikasi" description="Semua stok produk aman" compact />
                          ) : (
                            stockAlerts.map((alert) => {
                              const isRead = readAlerts.includes(alert.id);
                              const isOutOfStock = alert.type === 'out_of_stock';
                              return (
                                <div key={alert.id} onClick={() => handleAlertClick(alert)} data-testid={`stock-alert-item-${alert.id}`}
                                  className={`flex items-center px-4 py-3 cursor-pointer hover:bg-slate-50 dark:hover:bg-zinc-800/50 border-b border-slate-100 dark:border-zinc-800 last:border-0 ${!isRead ? 'bg-indigo-50/50 dark:bg-blue-900/10' : ''}`}>
                                  <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-3 ${isOutOfStock ? 'bg-red-100 dark:bg-red-900/30' : 'bg-amber-100 dark:bg-amber-900/30'}`}>
                                    <AlertTriangle className={`w-5 h-5 ${isOutOfStock ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`} />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-slate-800 dark:text-zinc-200 truncate">{alert.name}</p>
                                    <p className={`text-xs font-medium ${isOutOfStock ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`}>
                                      {isOutOfStock ? 'Stok habis!' : `Sisa ${alert.stock} unit`}
                                    </p>
                                  </div>
                                  {!isRead && <div className="w-2 h-2 rounded-full bg-indigo-600 ml-2" />}
                                </div>
                              );
                            })
                          )}
                        </div>
                        {stockAlerts.length > 0 && (
                          <div className="px-4 py-3 border-t border-slate-200 dark:border-zinc-800 bg-slate-50 dark:bg-zinc-900/50 rounded-b-xl">
                            <Link to="/catalog" onClick={() => setNotificationOpen(false)} className="text-sm text-indigo-600 hover:text-indigo-700 font-medium flex items-center justify-center gap-1 dark:text-indigo-400">
                              Lihat Semua Produk <ChevronRight className="w-4 h-4" />
                            </Link>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </header>

          {/* ================================================================ */}
          {/* MAIN CONTENT — tighter spacing on mobile */}
          {/* ================================================================ */}
          <main className="p-3 sm:p-6 space-y-3 sm:space-y-6">
            <WhatsAppEmbeddedSignup variant="banner" />
            
            {/* Quota Warning Banner (auto-shows at 80%+ usage) */}
            <QuotaWarningBanner data={planLimits} />
            
            {/* Setup Banner */}
            {onboardingSkipped && showSetupBanner && (
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-3 sm:p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-2.5">
                    <div className="w-9 h-9 rounded-lg bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center flex-shrink-0">
                      <Settings className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                    </div>
                    <div>
                      <h3 className="font-medium text-amber-900 dark:text-amber-100 text-sm">Lengkapi Setup Toko</h3>
                      <p className="text-xs text-amber-700 dark:text-amber-300 mt-0.5 hidden sm:block">
                        Beberapa fitur belum aktif. Selesaikan setup untuk mengoptimalkan bisnis Anda.
                      </p>
                      <div className="flex gap-2 mt-2">
                        <Button size="sm" onClick={handleResumeOnboarding} className="bg-amber-600 hover:bg-amber-700 text-white h-7 text-xs px-3">
                          <Settings className="w-3 h-3 mr-1" />Lanjutkan
                        </Button>
                        <Button size="sm" variant="ghost" onClick={dismissSetupBanner} className="text-amber-700 h-7 text-xs dark:text-amber-300">
                          Nanti
                        </Button>
                      </div>
                    </div>
                  </div>
                  <button onClick={dismissSetupBanner} className="text-amber-400 hover:text-amber-600 dark:hover:text-amber-400 flex-shrink-0 dark:text-amber-400">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {/* ============================================================ */}
            {/* KPI CARDS — compact on mobile */}
            {/* ============================================================ */}
            <section className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4" data-testid="kpi-section">
              <KPICard title="Revenue" value={formatCurrency(totalRevenue)} icon={DollarSign} color="blue" compact
                trendValue={weeklyRevenue[6] > weeklyRevenue[5] ? '+' + ((weeklyRevenue[6] - weeklyRevenue[5]) / Math.max(weeklyRevenue[5], 1) * 100).toFixed(0) + '%' : '-'}
                trend={weeklyRevenue[6] >= weeklyRevenue[5] ? 'up' : 'down'} trendLabel="vs kemarin" />
              <KPICard title="Total Pesanan" value={recentOrders.length} icon={ShoppingCart} color="green" compact
                trendValue={ordersToday >= ordersYesterday ? (ordersYesterday === 0 && ordersToday > 0 ? `+${ordersToday}` : `+${ordersToday - ordersYesterday}`) : `${ordersToday - ordersYesterday}`}
                trend={ordersToday >= ordersYesterday ? 'up' : 'down'} trendLabel="vs kemarin" />
              <KPICard title="Total Chat" value={totalChats} icon={MessageSquare} color="purple" compact
                trendValue={chatsToday >= chatsYesterday ? (chatsYesterday === 0 && chatsToday > 0 ? `+${chatsToday}` : `+${chatsToday - chatsYesterday}`) : `${chatsToday - chatsYesterday}`}
                trend={chatsToday >= chatsYesterday ? 'up' : 'down'} trendLabel="vs kemarin" />
              <DashboardCard className="!p-2.5 sm:!p-4">
                <ResponseTimeCard period={analyticsPeriod} channel={selectedChannel} />
              </DashboardCard>
            </section>

            {/* ============================================================ */}
            {/* MAIN GRID — 12 COLUMNS */}
            {/* ============================================================ */}
            <section className="grid grid-cols-12 gap-3 sm:gap-4 lg:gap-6">
              
              {/* LEFT COLUMN: Analytics (8 cols on lg) */}
              <div className="col-span-12 lg:col-span-8 space-y-3 sm:space-y-4 lg:space-y-6">
                
                {/* Revenue Trend + Order Status */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                  {/* Revenue Trend Chart — shorter on mobile */}
                  <DashboardCard className="overflow-visible !p-3 sm:!p-4">
                    <CardHeader 
                      title="Trend Revenue" icon={TrendingUp}
                      action={<span className="text-xs text-indigo-600 font-medium dark:text-indigo-400">{analyticsPeriod === 'today' ? 'Hari Ini' : analyticsPeriod === 'week' ? '7 Hari' : analyticsPeriod === 'month' ? '30 Hari' : 'Semua'}</span>}
                    />
                    <div className="flex items-end gap-1.5 h-24 sm:h-32 mb-2 sm:mb-3 overflow-visible">
                      {weeklyRevenue.map((rev, idx) => {
                        const maxRev = Math.max(...weeklyRevenue, 1);
                        const height = Math.max((rev / maxRev) * 100, rev > 0 ? 15 : 8);
                        const days = ['Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab', 'Min'];
                        return (
                          <div key={idx} className="flex-1 flex flex-col items-center h-full group relative"
                            onClick={(e) => {
                              const tooltip = e.currentTarget.querySelector('.revenue-tooltip');
                              if (tooltip && window.innerWidth < 640) {
                                document.querySelectorAll('.revenue-tooltip.mobile-show').forEach(t => { if (t !== tooltip) t.classList.remove('mobile-show'); });
                                tooltip.classList.toggle('mobile-show');
                              }
                            }}>
                            <div className="flex-1 w-full flex items-end justify-center">
                              <div className={`w-full max-w-[32px] rounded-t-md transition-all cursor-pointer ${idx === 6 ? 'bg-indigo-500' : rev > 0 ? 'bg-blue-200 dark:bg-indigo-600/50 group-hover:bg-blue-300' : 'bg-slate-200 dark:bg-zinc-800'}`} style={{ height: `${height}%` }} />
                            </div>
                            <span className="text-[9px] sm:text-[10px] text-slate-500 mt-1.5 dark:text-zinc-400">{days[idx]}</span>
                            <div className={`revenue-tooltip absolute z-50 pointer-events-none transition-opacity ${idx === 6 ? 'right-[calc(100%+4px)] top-0' : idx === 0 ? '-top-8 left-0' : '-top-8 left-1/2 -translate-x-1/2'} opacity-0 sm:group-hover:opacity-100 [&.mobile-show]:opacity-100`}>
                              <div className="bg-slate-900 text-white text-xs px-2 py-1 rounded shadow-lg whitespace-nowrap min-w-max">Rp {rev.toLocaleString('id-ID')}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex items-center justify-between pt-2 sm:pt-3 border-t border-slate-100 dark:border-zinc-800">
                      <span className="text-[10px] sm:text-xs text-slate-500 dark:text-zinc-400">{analyticsPeriod === 'today' ? 'Total Hari Ini' : analyticsPeriod === 'week' ? 'Total Minggu Ini' : analyticsPeriod === 'month' ? 'Total Bulan Ini' : 'Total Revenue'}</span>
                      <span className="text-xs sm:text-sm font-bold text-indigo-600 dark:text-indigo-400">Rp {totalRevenue.toLocaleString('id-ID')}</span>
                    </div>
                  </DashboardCard>

                  {/* Order Status */}
                  <DashboardCard className="!p-3 sm:!p-4">
                    <CardHeader title="Status Pesanan" icon={Package} />
                    <div className="overflow-y-auto max-h-[200px] sm:max-h-[260px]">
                    {(() => {
                      const belumBayar = recentOrders.filter(o => ['Menunggu Pembayaran', 'pending', 'Pending'].includes(o.status)).length;
                      const verifikasi = recentOrders.filter(o => ['Verifikasi', 'verifikasi'].includes(o.status)).length;
                      const paid = recentOrders.filter(o => ['paid', 'Paid'].includes(o.status)).length;
                      const shipped = recentOrders.filter(o => ['shipped', 'Shipped'].includes(o.status)).length;
                      const completed = recentOrders.filter(o => ['completed', 'Completed', 'Selesai'].includes(o.status)).length;
                      const cancelled = recentOrders.filter(o => ['cancelled', 'Cancelled', 'Dibatalkan'].includes(o.status)).length;
                      const total = recentOrders.length || 1;
                      
                      if (recentOrders.length === 0) {
                        return <EmptyState icon={Package} title="Belum ada pesanan" description="Pesanan akan muncul di sini" action={() => navigate('/inbox')} actionLabel="Buka Inbox" compact />;
                      }
                      
                      return (
                        <div className="space-y-3 sm:space-y-4">
                          {[
                            { label: 'Belum Bayar', value: belumBayar, color: 'bg-amber-500', total },
                            { label: 'Verifikasi', value: verifikasi, color: 'bg-orange-500', total },
                            { label: 'Dibayar', value: paid, color: 'bg-indigo-500', total },
                            // Toko produk digital tidak punya tahap "Dikirim".
                            ...(user?.business_type === 'digital' && !shipped
                              ? []
                              : [{ label: 'Dikirim', value: shipped, color: 'bg-cyan-500', total }]),
                            { label: 'Selesai', value: completed, color: 'bg-green-500', total },
                            { label: 'Dibatalkan', value: cancelled, color: 'bg-red-500', total }
                          ].map((item) => (
                            <div key={item.label} className="flex items-center gap-2 sm:gap-3">
                              <div className={`w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full ${item.color}`} />
                              <span className="text-xs sm:text-sm text-slate-600 dark:text-zinc-400 flex-1">{item.label}</span>
                              <span className="text-xs sm:text-sm font-semibold text-slate-900 dark:text-zinc-50 w-8 text-right">{item.value}</span>
                              <div className="w-16 sm:w-24 h-1.5 sm:h-2 bg-slate-100 dark:bg-zinc-800 rounded-full overflow-hidden">
                                <div className={`h-full ${item.color} rounded-full transition-all`} style={{ width: `${(item.value/total)*100}%` }} />
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                    </div>
                  </DashboardCard>
                </div>

                {/* Business Analytics */}
                <DashboardCard className="!p-3 sm:!p-4">
                  <CardHeader title="Analisis Bisnis" icon={BarChart3} />
                  <div>
                    <DashboardAnalytics period={analyticsPeriod} channel={selectedChannel} />
                  </div>
                </DashboardCard>

                {/* Conversion Funnel + Customer Insights */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                  <DashboardCard className="!p-3 sm:!p-4">
                    <div className="overflow-y-auto max-h-[350px] sm:max-h-[450px]">
                      <ConversionFunnel period={analyticsPeriod} channel={selectedChannel} />
                    </div>
                  </DashboardCard>
                  <DashboardCard className="!p-3 sm:!p-4">
                    <div className="overflow-y-auto max-h-[350px] sm:max-h-[450px]">
                      <CustomerInsights period={analyticsPeriod} channel={selectedChannel} />
                    </div>
                  </DashboardCard>
                </div>
              </div>

              {/* RIGHT SIDEBAR (4 cols on lg) */}
              <div className="col-span-12 lg:col-span-4 flex flex-col gap-3 sm:gap-4 lg:gap-6">
                
                {/* AI Quota Card */}
                <QuotaCard data={planLimits} />
                
                {/* AI Agents */}
                <DashboardCard noPadding>
                  <div className="px-3 sm:px-4 py-2.5 sm:py-3 border-b border-slate-200 dark:border-zinc-800 flex items-center justify-between">
                    <CardHeader title="AI Agents" icon={Bot} className="mb-0" />
                    <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                      <DialogTrigger asChild>
                        <button className="w-7 h-7 rounded-lg bg-indigo-500 hover:bg-indigo-600 flex items-center justify-center text-white transition-colors" data-testid="create-agent-btn">
                          <Plus className="w-4 h-4" />
                        </button>
                      </DialogTrigger>
                      <DialogContent className="sm:max-w-[425px]">
                        <DialogHeader><DialogTitle>Buat AI Agent Baru</DialogTitle></DialogHeader>
                        <div className="grid gap-4 py-4">
                          <div className="grid gap-2">
                            <Label htmlFor="name">Nama Agent</Label>
                            <Input id="name" value={newBotName} onChange={(e) => setNewBotName(e.target.value)} placeholder="Contoh: CS Toko Online" />
                          </div>
                          <div className="grid gap-2">
                            <Label htmlFor="description">Deskripsi (opsional)</Label>
                            <Textarea id="description" value={newBotDescription} onChange={(e) => setNewBotDescription(e.target.value)} placeholder="Deskripsi singkat" />
                          </div>
                        </div>
                        <div className="flex justify-end gap-3">
                          <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Batal</Button>
                          <Button onClick={createAiAgent} disabled={creating} className="bg-indigo-600 hover:bg-indigo-700">{creating ? 'Membuat...' : 'Buat'}</Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                  <div className="divide-y divide-slate-100 dark:divide-zinc-800 max-h-[175px] overflow-y-auto">
                    {aiAgents.length === 0 ? (
                      <EmptyState icon={Bot} title="Belum ada agent" description="Buat AI Agent untuk mulai" action={() => setCreateDialogOpen(true)} actionLabel="Buat Agent" compact />
                    ) : (
                      aiAgents.map((bot) => (
                        <div key={bot.id} className="px-3 sm:px-4 py-2.5 sm:py-3 hover:bg-slate-50 dark:hover:bg-zinc-800/50 group dark:bg-zinc-900">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                              <Bot className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-slate-900 dark:text-zinc-50 truncate">{bot.name}</p>
                              <p className="text-xs text-slate-500 dark:text-zinc-400">{bot.total_chats || 0} chat</p>
                            </div>
                            <Link to={`/agent/${bot.id}`} className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 hover:bg-slate-100 dark:hover:bg-slate-600 rounded-lg dark:bg-zinc-800">
                              <ArrowUpRight className="w-4 h-4 text-slate-500 dark:text-zinc-400" />
                            </Link>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </DashboardCard>

                {/* Channel Performance */}
                <DashboardCard className="!p-3 sm:!p-4">
                  <div>
                    <ChannelPerformance period={analyticsPeriod} channel={selectedChannel} />
                  </div>
                </DashboardCard>

                {/* Recent Orders */}
                <DashboardCard noPadding className="lg:flex-1 lg:flex lg:flex-col">
                  <div className="px-3 sm:px-4 py-2.5 sm:py-3 border-b border-slate-200 dark:border-zinc-800 flex items-center justify-between">
                    <CardHeader title="Pesanan Terbaru" icon={ShoppingCart} className="mb-0" />
                    <Link to="/orders" className="text-xs text-indigo-600 hover:text-indigo-700 font-medium flex items-center gap-1 dark:text-indigo-400">
                      Semua <ChevronRight className="w-3 h-3" />
                    </Link>
                  </div>
                  <div className="divide-y divide-slate-100 dark:divide-zinc-800 lg:flex-1 overflow-y-auto">
                    {expandedRecentOrders.length === 0 ? (
                      <EmptyState icon={ShoppingCart} title="Belum ada pesanan" description="Pesanan dari customer akan muncul di sini" compact />
                    ) : (
                      expandedRecentOrders.slice(0, 5).map((item) => (
                        <div key={item.id} className="px-3 sm:px-4 py-2.5 sm:py-3 hover:bg-slate-50 dark:hover:bg-zinc-800/50 dark:bg-zinc-900">
                          <div className="flex items-center justify-between gap-2">
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium text-slate-900 dark:text-zinc-50 truncate">{item.customer_name || 'Customer'}</p>
                              <p className="text-xs text-slate-500 truncate dark:text-zinc-400">
                                {item.product}
                                {item.item_count > 1 && <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-indigo-100 text-indigo-700 dark:text-indigo-300 dark:bg-indigo-950/40">{item.item_count} item</span>}
                              </p>
                            </div>
                            <p className="text-xs sm:text-sm font-semibold text-indigo-600 whitespace-nowrap shrink-0 dark:text-indigo-400">Rp {(item.total || 0).toLocaleString('id-ID')}</p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </DashboardCard>
              </div>
            </section>

            {/* AI INSIGHTS — FULL WIDTH BOTTOM */}
            <section>
              <AIInsightsWidget />
            </section>
          </main>
        </div>
      </div>
      
      <PWAInstallBanner />
    </div>
  );
};

export default Dashboard;
